// Copyright (c) 2025 Juan Beltrán
// Licensed under the MIT License. See LICENSE file for details.

import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { getRedirectResult, onAuthStateChanged } from 'firebase/auth';
import { getFirestore, doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { auth } from './utils/firebase';
import { useStore } from './store/useStore';
import { Home } from './pages/Home';
import { Success } from './pages/Success';

export default function App() {
  const { setUser } = useStore();

  useEffect(() => {
    // Handle both redirect result and auth state changes
    const handleAuth = async () => {
      try {
        // First check for redirect result
        const result = await getRedirectResult(auth);
        if (result) {
          // Avoid logging sensitive user identifiers
          console.log('Redirect result successful');
          setUser(result.user);
          
          const db = getFirestore();
          const userRef = doc(db, 'users', result.user.uid);
          const userDoc = await getDoc(userRef);
          
          if (!userDoc.exists()) {
            console.log('New user, initializing data');
            await setDoc(userRef, {
              language: 'en',
              createdAt: serverTimestamp(),
              topUpValue: 1
            });
          }
        }

        // Then set up auth state listener
        const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
          if (firebaseUser) {
            // Avoid logging sensitive user identifiers
            console.log('Auth state changed - user logged in');
            setUser(firebaseUser);
          } else {
            console.log('Auth state changed - no user');
            setUser(null);
          }
        });

        return () => unsubscribe();
      } catch (error) {
        // Sanitize errors before logging
        const errorMessage = error instanceof Error ? 
          error.message.replace(/uid|email|token|key/gi, '[REDACTED]') : 
          'Unknown authentication error';
        console.error('Auth error:', errorMessage);
      }
    };

    handleAuth();
  }, [setUser]);

  return (
    <Router>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/success" element={<Success />} />
        {/* <Route path="*" element={<NotFound />} /> */}
      </Routes>
    </Router>
  );
}